/**
 * Terminal 82 - Winner Banner
 * 
 * Shows a celebratory banner when user has won a prediction
 */

export interface WinnerBannerData {
  date: string;
  dayId: number;
  claimed: boolean;
}

const BANNER_DISMISSED_KEY = 'terminal82_banner_dismissed';

/**
 * Check if banner was dismissed for this win
 */
function isDismissed(date: string): boolean {
  try {
    const dismissed = localStorage.getItem(BANNER_DISMISSED_KEY);
    if (!dismissed) return false;
    const dates = JSON.parse(dismissed) as string[];
    return dates.includes(date);
  } catch {
    return false;
  }
}

/**
 * Mark banner as dismissed
 */
function dismissBanner(date: string): void {
  try {
    const dismissed = localStorage.getItem(BANNER_DISMISSED_KEY);
    const dates = dismissed ? JSON.parse(dismissed) as string[] : [];
    if (!dates.includes(date)) {
      dates.push(date);
      // Keep only last 10 dismissed dates
      while (dates.length > 10) dates.shift();
      localStorage.setItem(BANNER_DISMISSED_KEY, JSON.stringify(dates));
    }
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Format date for display (e.g., "JAN 30 2026")
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00Z');
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  return `${months[date.getUTCMonth()]} ${date.getUTCDate()} ${date.getUTCFullYear()}`;
}

/**
 * Create and show winner banner
 */
export function showWinnerBanner(data: WinnerBannerData): HTMLElement | null {
  // Check if already dismissed
  if (isDismissed(data.date)) {
    return null;
  }

  // Remove any existing banner
  const existing = document.getElementById('winner-banner');
  if (existing) existing.remove();

  // Create banner
  const banner = document.createElement('div');
  banner.id = 'winner-banner';
  banner.className = 'winner-banner';
  banner.innerHTML = `
    <div class="winner-banner-content">
      <div class="winner-banner-icon">🏆</div>
      <div class="winner-banner-text">
        <div class="winner-banner-title">YOU WON!</div>
        <div class="winner-banner-subtitle">
          Day #${data.dayId} · ${formatDate(data.date)}
        </div>
      </div>
      <div class="winner-banner-actions">
        <a href="https://opensea.io/assets/ethereum/0x48d477052a585eb96ff70f4c0c102a214233d039/${data.dayId}" 
           target="_blank" 
           rel="noopener noreferrer"
           class="winner-banner-btn winner-banner-btn-primary">
          View NFT →
        </a>
        <button class="winner-banner-btn winner-banner-btn-dismiss" id="winner-banner-dismiss">
          ✕
        </button>
      </div>
    </div>
  `;

  // Add dismiss handler
  banner.querySelector('#winner-banner-dismiss')?.addEventListener('click', () => {
    dismissBanner(data.date);
    banner.classList.add('winner-banner-hiding');
    setTimeout(() => banner.remove(), 300);
  });

  // Insert at top of body
  document.body.insertBefore(banner, document.body.firstChild);

  // Animate in
  requestAnimationFrame(() => {
    banner.classList.add('winner-banner-visible');
  });

  return banner;
}

/**
 * Hide winner banner (if visible)
 */
export function hideWinnerBanner(): void {
  const banner = document.getElementById('winner-banner');
  if (banner) {
    banner.classList.add('winner-banner-hiding');
    setTimeout(() => banner.remove(), 300);
  }
}
